import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Swaps the PWA "install identity" per route so adding the Site Diary to the
 * home screen yields the "LUX Diary" clipboard icon + name, while the rest of
 * the app keeps its X icon + "LUX Ops". iOS reads the apple-touch-icon +
 * apple-mobile-web-app-title at add-to-home time; Android reads the manifest.
 * The two installs never overwrite each other because each manifest has a
 * distinct `id` ("/" vs "/diary").
 */
const MAIN = { manifest: '/manifest.json', appleIcon: '/logo192.png', title: 'LUX Ops', doc: 'LUX Developments' };
const DIARY = { manifest: '/manifest-diary.json', appleIcon: '/diary-logo192.png', title: 'LUX Diary', doc: 'LUX Diary' };

const isDiaryPath = (p) => /^\/(portal\/)?diary(\/|$)/.test(p || '');

/**
 * Move between the ops app and the diary shell. iOS only reads the manifest
 * that was present at page load, so on touch devices crossing the shell
 * boundary is a full load — "Add to Home Screen" then installs the right app.
 * Desktop keeps the instant client-side route.
 */
export function switchShell(navigate, path) {
  const crossing = isDiaryPath(path) !== isDiaryPath(window.location.pathname);
  const touch = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
  if (crossing && touch) {
    window.location.assign(path);
    return;
  }
  navigate(path);
}

function ensureTag(id, tagName, setup) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement(tagName);
    el.id = id;
    document.head.appendChild(el);
    setup(el);
  }
  return el;
}

export function InstallIdentity() {
  const { pathname } = useLocation();
  useEffect(() => {
    const cfg = isDiaryPath(pathname) ? DIARY : MAIN;

    ensureTag('app-manifest', 'link', (el) => { el.rel = 'manifest'; }).setAttribute('href', cfg.manifest);
    ensureTag('app-touch-icon', 'link', (el) => { el.rel = 'apple-touch-icon'; }).setAttribute('href', cfg.appleIcon);
    ensureTag('app-web-title', 'meta', (el) => { el.name = 'apple-mobile-web-app-title'; }).setAttribute('content', cfg.title);
    document.title = cfg.doc;
  }, [pathname]);

  return null;
}

export default InstallIdentity;
